import { AnyAction, Store } from 'redux';
import {
  ApolloClient,
  ApolloLink,
  FetchResult,
  InMemoryCache,
  Observable,
  Operation,
  createHttpLink,
  from,
  split,
} from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { Client, ClientOptions, createClient } from 'graphql-ws';
import { StoreType } from '@tock/shared/utils/connect';
import { getBusinessHeader, getShippingStateHeader } from '@tock/shared/utils/AjaxActions';
import { getMainDefinition } from '@apollo/client/utilities';
import { graphqlError } from 'actions/AppActions';
import { onError } from '@apollo/client/link/error';
import { print } from 'graphql';
import { showError } from '@tock/toast';
import InMemoryCacheConfig from 'generated/graphql';
import _ from 'lodash';
import fetch from 'cross-fetch';

// WebSocketLink definiton is copy-pasta from dashboard/src/js/initializeApolloLink

// Ping the server every 10 seconds to make sure the connection is still alive
const KEEP_ALIVE_TIME_MS = 10_000;

// After sending a ping, wait up to 5 seconds to receive a pong from the server
// before assuming the connection is dead and starting a new one
const WAIT_FOR_PONG_MS = 5_000;

// After all subscriptions are complete, wait 10 seconds before closing the
// connection. This allows a user quickly hopping between realtime and
// non-realtime pages to re-use a connection rather than creating a new one.
const CONNECTION_CLOSE_DEBOUNCE_MS = 10_000;

class WebSocketLink extends ApolloLink {
  private client: Client;
  private timeout?: ReturnType<typeof setTimeout>;
  private activeWebSocket?: WebSocket;

  constructor(options: ClientOptions) {
    super();
    // ProtocolFreeWebSocket is defined in a separate module, and dynamically require()d, so as not
    // to disturb server-side rendering machinery.
    const { ProtocolFreeWebSocket } = require('utils/protocolFreeWebSocket');
    this.client = createClient({
      ...options,
      webSocketImpl: ProtocolFreeWebSocket,
      keepAlive: KEEP_ALIVE_TIME_MS,
      lazyCloseTimeout: CONNECTION_CLOSE_DEBOUNCE_MS,
      retryAttempts: 10, // Uses randomized exponential backoff. A value of 10 gives up after ~17 minutes.
      isFatalConnectionProblem: (err) => {
        // Retry if there's an Event, which usually indicates something connection-related.
        // Consider an issue fatal (and don't retry) if the websocket server explicitly sends an error.
        return !(err instanceof Event);
      },
      on: {
        opened: (socket: unknown) => {
          if (socket instanceof WebSocket) {
            this.activeWebSocket = socket;
          }
        },
        ping: (received: boolean) => {
          const sent = !received;
          if (sent) {
            this.timeout = setTimeout(() => {
              if (this.activeWebSocket?.readyState === WebSocket.OPEN) {
                this.activeWebSocket.close(4408, 'Request Timeout');
              }
            }, WAIT_FOR_PONG_MS);
          }
        },
        pong: (received: boolean) => {
          if (received && this.timeout) {
            clearTimeout(this.timeout);
          }
        },
      },
    });
  }

  public request(operation: Operation): Observable<FetchResult> {
    return new Observable((sink) => {
      return this.client.subscribe<FetchResult>(
        { ...operation, query: print(operation.query) },
        {
          next: sink.next.bind(sink),
          complete: sink.complete.bind(sink),
          error: sink.error.bind(sink),
        }
      );
    });
  }
}

export function initializeApolloClient(store: Store, additionalHeaders: Dictionary<string> = {}) {
  const state = store.getState();
  const app = state.app as StoreType['app'];
  const dispatch = store.dispatch;

  const apolloClient = new ApolloClient({
    link: createLinkChain(
      app,
      dispatch,
      { batchLinkEnabled: state.features.features.CONSUMER_GRAPHQL_REQUEST_BATCHING },
      additionalHeaders
    ),
    cache:
      typeof window !== 'undefined' && !_.isEmpty(window.$APOLLO_STATE)
        ? new InMemoryCache(InMemoryCacheConfig).restore(window.$APOLLO_STATE)
        : new InMemoryCache(InMemoryCacheConfig),
  });

  return apolloClient;
}

export function createLinkChain(
  app: StoreType['app'],
  dispatch: Dispatch<AnyAction>,
  { batchLinkEnabled }: { batchLinkEnabled: boolean },
  additionalHeaders: Dictionary<string> = {}
) {
  const apolloAuth = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => {
      return {
        headers: {
          ...headers,
          ...additionalHeaders,
          ...getBusinessHeader(app),
          ...getShippingStateHeader(app.shippingState),
        },
      };
    });
    return forward(operation);
  });

  const apolloError = onError(
    ({ operation: { operationName, variables }, graphQLErrors, networkError }) => {
      if (networkError) {
        if (typeof window === 'undefined') {
          dispatch(graphqlError(networkError));
        } else {
          if (window?.LOGGER && (graphQLErrors || networkError)) {
            if (graphQLErrors) {
              window.LOGGER?.error(graphQLErrors);
            } else {
              window.LOGGER?.error(networkError.message, networkError);
            }
          }
          dispatch(showError('Failed to connect, check your internet and try again.'));
        }
      }
    }
  );

  const apolloUri = `${app.settings.API_BASE ?? 'https://staging.exploretock.com'}/graphql`; // in tests, this value is undefined so default to staging

  const apolloHttpLink = createHttpLink({
    uri: apolloUri,
    credentials: 'include',
    fetch: (uri, options) => {
      let opName = 'UnknownOperation';
      if (typeof options?.body === 'string') {
        const { operationName } = JSON.parse(options?.body);
        if (operationName) {
          opName = operationName;
        }
      }
      return fetch(`${uri}/${opName}`, options);
    },
  });
  let effectiveApolloLink = apolloHttpLink;

  const apolloBatchHttpLink = new BatchHttpLink({
    uri: apolloUri,
    credentials: 'include',
    fetch: (uri, options) => {
      if (typeof options?.body !== 'string') {
        throw new Error('Options body must be a string');
      }

      const operations = JSON.parse(options?.body);

      const operationNames: string[] = operations.map(
        (operation: { operationName: string }) => operation.operationName || 'UnknownOperation'
      );
      if (operationNames.length === 1) {
        // When there's only one operation, fall back to using the standard
        // GraphQL handler that responds to a single request.
        const body = JSON.stringify(operations[0]);
        return fetch(`${uri}/${operationNames[0]}`, { ...options, body });
      }
      return fetch(`${uri}/batch/${operationNames.sort().join('+')}`, options);
    },
  });

  // If running on the client, let's do actual websocket handling.
  if (typeof window !== 'undefined') {
    const apolloWebSocketLink = new WebSocketLink({
      url: `${app.settings.WSS_BASE}/graphql?auth=${app.jwtToken}`,
    });

    // Use either the standard HTTP link or the WebSocket link based on the type
    // of query that's getting sent.
    effectiveApolloLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      apolloWebSocketLink,
      batchLinkEnabled ? apolloBatchHttpLink : apolloHttpLink
    );
  }

  return from([apolloAuth, apolloError, effectiveApolloLink]);
}
